import { useCodeMirror } from '@uiw/react-codemirror';
import React, { useEffect, useRef } from 'react';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { html as langHtml } from '@codemirror/lang-html';
import { minimalSetup } from '@uiw/codemirror-extensions-basic-setup';
import { standardKeymap } from '@codemirror/commands';
import { tags as t } from '@lezer/highlight';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import * as events from '@uiw/codemirror-extensions-events';
import { $createRangeSelection, $setSelection } from 'lexical';

const editorLightCSS = EditorView.theme({
  '&.cm-editor': {
    background: '#fbf9f7',
  },
  '&.cm-focused': {
    outline: '0',
  },
  '&.cm-editor .cm-content': {
    padding: '7px 0',
  },
  '&.cm-editor .cm-content, &.cm-editor .cm-gutter': {
    minHeight: '64px',
  },
  '&.cm-editor .cm-scroller': {
    overflow: 'auto',
  },
  '&.cm-editor .cm-gutters': {
    background: 'none',
    border: 'none',
    fontFamily: 'Consolas,Liberation Mono,Menlo,Courier,monospace;',
    color: '#CED4D9',
    fontSize: '0.8rem',
    lineHeight: '1.25rem',
  },
  '&.cm-editor .cm-lineNumbers': {
    padding: '0',
  },
  '&.cm-editor .cm-foldGutter': {
    width: '0',
  },
  '&.cm-editor .cm-line': {
    padding: '0 .8rem',
    color: '#394047',
    fontFamily: 'Consolas,Liberation Mono,Menlo,Courier,monospace;',
    fontSize: '0.8rem',
    lineHeight: '1.25rem',
  },
  '&.cm-editor .cm-activeLine, &.cm-editor .cm-activeLineGutter': {
    background: 'none',
  },
  '&.cm-editor .cm-cursor, &.cm-editor .cm-dropCursor': {
    borderLeft: '1.2px solid black',
  },
});

const editorLightHighlightStyle = HighlightStyle.define([
  { tag: t.keyword, color: '#5A5CAD' },
  { tag: t.atom, color: '#6C8CD5' },
  { tag: t.number, color: '#116644' },
  { tag: t.definition(t.variableName), textDecoration: 'underline' },
  { tag: t.variableName, color: 'black' },
  {
    tag: t.comment,
    color: '#0080FF',
    fontStyle: 'italic',
    background: 'rgba(0,0,0,.05)',
  },
  { tag: [t.string, t.special(t.brace)], color: '#183691' },
  { tag: t.meta, color: 'yellow' },
  { tag: t.bracket, color: '#63a35c' },
  { tag: t.tagName, color: '#63a35c' },
  { tag: t.attributeName, color: '#795da3' },
]);

export default function HtmlEditor({
  html,
  updateHtml,
  escapeUp,
  escapeDown,
  removeNode,
}) {
  const [lexicalEditor] = useLexicalComposerContext();

  const editor = useRef();

  const onChange = React.useCallback(
    value => {
      updateHtml(value);
    },
    [updateHtml],
  );

  // Base extensions for the CodeMirror editor
  const extensions = [
    editorLightCSS,
    syntaxHighlighting(editorLightHighlightStyle), // customizes syntax highlighting rules
    EditorView.lineWrapping, // wraps lines that exceed the viewport width
    lineNumbers(), // adds line numbers to the gutter
    minimalSetup({ defaultKeymap: false }), // disable defaultKeymap to prevent Mod+Enter from inserting new line
    keymap.of(closeBracketsKeymap), // required for quote completion; needs to be listed before standardKeymap
    keymap.of(standardKeymap), // add back in standardKeymap, which doesn't include Mod+Enter
    keymap.of([
      {
        key: 'ArrowUp',
        run: () => {
          // insert paragraph above if needed
          escapeUp();

          // blur editor to focus on previous paragraph
          editor.contentDOM.blur();

          return false;
        },
      },
      {
        key: 'ArrowDown',
        run: () => {
          // insert paragraph below if needed
          escapeDown();

          // blur editor to focus on next paragraph
          editor.contentDOM.blur();

          return false;
        },
      },
      {
        key: 'Mod-z',
        stopPropagation: true,
      },
      {
        key: 'Mod-y',
        stopPropagation: true,
      },
      {
        key: 'Backspace',
        run: editorView => {
          // if editor has no content, remove node from lexical editor
          if (editorView.state.doc.toString() === '') {
            removeNode();
          }

          return false;
        },
      },
    ]),
    langHtml(),
    closeBrackets(),
    events.content({
      // remove selection from lexical editor when CodeMirror editor is focused
      focus: () => {
        lexicalEditor.update(() => {
          $setSelection(null);
        });
      },
      blur: () => {
        lexicalEditor.update(() => {
          // Set a range selection
          const rangeSelection = $createRangeSelection();
          $setSelection(rangeSelection);
        });
      },
    }),
  ];

  const { setContainer } = useCodeMirror({
    value: html,
    container: editor.current,
    extensions: extensions,
    autoFocus: true,
    basicSetup: false,
    onChange: onChange,
  });

  useEffect(() => {
    if (editor.current) {
      setContainer(editor.current);
    }
  }, [editor.current]);

  return (
    <div className="my-4">
      <div ref={editor} />
    </div>
  );
}
